/**
 * Advanced Video Optimization Utilities
 * Additional techniques for maximizing YouTube video loading performance
 */

export interface AdvancedVideoOptimizationOptions {
  enablePreconnect?: boolean;
  enableDNSPrefetch?: boolean;
  enableResourceHints?: boolean;
  enableServiceWorker?: boolean;
  enablePredictiveLoading?: boolean;
  enableCompression?: boolean;
  enableCaching?: boolean;
  enableBackgroundPreload?: boolean;
}

// YouTube domains for preconnect optimization
const YOUTUBE_DOMAINS = [
  "https://www.youtube.com",
  "https://www.youtube-nocookie.com",
  "https://i.ytimg.com",
  "https://img.youtube.com",
  "https://www.google.com",
  "https://www.gstatic.com",
];

/**
 * Add resource hints for YouTube domains
 */
export function addYouTubeResourceHints(): void {
  if (typeof document === "undefined") return;

  const head = document.head;

  // Remove existing hints to avoid duplicates
  const existingHints = head.querySelectorAll(
    'link[rel*="preconnect"], link[rel*="dns-prefetch"]',
  );
  existingHints.forEach((hint) => hint.remove());

  YOUTUBE_DOMAINS.forEach((domain) => {
    // Preconnect for critical domains
    if (domain.includes("youtube.com") || domain.includes("ytimg.com")) {
      const preconnect = document.createElement("link");
      preconnect.rel = "preconnect";
      preconnect.href = domain;
      preconnect.crossOrigin = "anonymous";
      head.appendChild(preconnect);
    }

    // DNS prefetch for all domains
    const dnsPrefetch = document.createElement("link");
    dnsPrefetch.rel = "dns-prefetch";
    dnsPrefetch.href = domain;
    head.appendChild(dnsPrefetch);
  });

  console.log("🔗 YouTube resource hints added");
}

/**
 * Preload critical YouTube resources
 */
export function preloadCriticalYouTubeResources(): void {
  if (typeof document === "undefined") return;

  const head = document.head;

  // Preload YouTube embed script
  const embedScript = document.createElement("link");
  embedScript.rel = "preload";
  embedScript.as = "script";
  embedScript.href = "https://www.youtube.com/iframe_api";
  head.appendChild(embedScript);

  // Preload YouTube CSS
  const youtubeCSS = document.createElement("link");
  youtubeCSS.rel = "preload";
  youtubeCSS.as = "style";
  youtubeCSS.href = "https://www.youtube.com/s/desktop/";
  head.appendChild(youtubeCSS);

  console.log("📦 Critical YouTube resources preloaded");
}

/**
 * Initialize YouTube iframe API
 */
export function initializeYouTubeAPI(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === "undefined") {
      resolve();
      return;
    }

    // Check if API is already loaded
    if (window.YT && window.YT.Player) {
      resolve();
      return;
    }

    // Load YouTube iframe API
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName("script")[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

    // Set up callback
    window.onYouTubeIframeAPIReady = () => {
      console.log("🎬 YouTube iframe API loaded");
      resolve();
    };

    // Fallback timeout
    setTimeout(() => {
      if (!window.YT) {
        console.warn("⚠️ YouTube API failed to load, using fallback");
        resolve();
      }
    }, 5000);
  });
}

/**
 * Advanced thumbnail optimization with multiple quality fallbacks
 */
export class AdvancedThumbnailOptimizer {
  private static instance: AdvancedThumbnailOptimizer;
  private cache = new Map<string, string>();
  private loadingPromises = new Map<string, Promise<string>>();

  static getInstance(): AdvancedThumbnailOptimizer {
    if (!AdvancedThumbnailOptimizer.instance) {
      AdvancedThumbnailOptimizer.instance = new AdvancedThumbnailOptimizer();
    }
    return AdvancedThumbnailOptimizer.instance;
  }

  /**
   * Get optimized thumbnail with fallback strategy
   */
  async getOptimizedThumbnail(
    videoId: string,
    preferredQuality: "maxres" | "hq" | "mq" | "default" = "hq",
  ): Promise<string> {
    // Check cache first
    if (this.cache.has(videoId)) {
      return this.cache.get(videoId)!;
    }

    // Check if already loading
    if (this.loadingPromises.has(videoId)) {
      return this.loadingPromises.get(videoId)!;
    }

    // Start loading with fallback strategy
    const loadingPromise = this.loadThumbnailWithFallback(
      videoId,
      preferredQuality,
    );
    this.loadingPromises.set(videoId, loadingPromise);

    try {
      const thumbnailUrl = await loadingPromise;
      this.cache.set(videoId, thumbnailUrl);
      this.loadingPromises.delete(videoId);
      return thumbnailUrl;
    } catch (error) {
      this.loadingPromises.delete(videoId);
      throw error;
    }
  }

  private async loadThumbnailWithFallback(
    videoId: string,
    preferredQuality: string,
  ): Promise<string> {
    const qualities = ["maxres", "hq", "mq", "default"];
    const startIndex = qualities.indexOf(preferredQuality);

    for (let i = startIndex; i < qualities.length; i++) {
      const quality = qualities[i];
      const url = `https://img.youtube.com/vi/${videoId}/${quality}default.jpg`;

      try {
        const isAvailable = await this.checkImageAvailability(url);
        if (isAvailable) {
          console.log(`📹 Thumbnail loaded: ${videoId} (${quality})`);
          return url;
        }
      } catch (error) {
        console.warn(
          `Failed to load ${quality} thumbnail for ${videoId}:`,
          error,
        );
      }
    }

    throw new Error(`No thumbnail available for video ${videoId}`);
  }

  private checkImageAvailability(url: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }

  /**
   * Preload thumbnails in background
   */
  async preloadThumbnails(videoIds: string[]): Promise<void> {
    const promises = videoIds.map((videoId) =>
      this.getOptimizedThumbnail(videoId, "hq").catch(() => null),
    );

    await Promise.allSettled(promises);
    console.log(`📹 Preloaded ${videoIds.length} thumbnails`);
  }
}

/**
 * Advanced connection optimization
 */
export class ConnectionOptimizer {
  private static instance: ConnectionOptimizer;
  private connectionInfo: any = null;

  static getInstance(): ConnectionOptimizer {
    if (!ConnectionOptimizer.instance) {
      ConnectionOptimizer.instance = new ConnectionOptimizer();
    }
    return ConnectionOptimizer.instance;
  }

  /**
   * Get detailed connection information
   */
  getConnectionInfo() {
    if (this.connectionInfo) return this.connectionInfo;

    if ("connection" in navigator) {
      const connection = (navigator as any).connection;
      this.connectionInfo = {
        effectiveType: connection.effectiveType || "unknown",
        downlink: connection.downlink || 0,
        rtt: connection.rtt || 0,
        saveData: connection.saveData || false,
        type: connection.type || "unknown",
      };
    } else {
      this.connectionInfo = {
        effectiveType: "unknown",
        downlink: 0,
        rtt: 0,
        saveData: false,
        type: "unknown",
      };
    }

    return this.connectionInfo;
  }

  /**
   * Get optimal settings based on connection
   */
  getOptimalSettings() {
    const info = this.getConnectionInfo();

    if (info.saveData) {
      return {
        quality: "default",
        preload: false,
        lazyLoad: true,
        autoplay: false,
        muted: false,
        enablePredictiveLoading: false,
      };
    }

    switch (info.effectiveType) {
      case "slow-2g":
      case "2g":
        return {
          quality: "default",
          preload: false,
          lazyLoad: true,
          autoplay: false,
          muted: false,
          enablePredictiveLoading: false,
        };
      case "3g":
        return {
          quality: "mq",
          preload: true,
          lazyLoad: true,
          autoplay: false,
          muted: false,
          enablePredictiveLoading: true,
        };
      case "4g":
      default:
        return {
          quality: "hq",
          preload: true,
          lazyLoad: false,
          autoplay: true,
          muted: true,
          enablePredictiveLoading: true,
        };
    }
  }
}

/**
 * Predictive loading based on user behavior
 */
export class PredictiveLoader {
  private static instance: PredictiveLoader;
  private userBehavior = new Map<string, number>();
  private predictionThreshold = 0.7;

  static getInstance(): PredictiveLoader {
    if (!PredictiveLoader.instance) {
      PredictiveLoader.instance = new PredictiveLoader();
    }
    return PredictiveLoader.instance;
  }

  /**
   * Record user interaction with video
   */
  recordInteraction(videoId: string): void {
    const currentCount = this.userBehavior.get(videoId) || 0;
    this.userBehavior.set(videoId, currentCount + 1);
  }

  /**
   * Get videos likely to be watched next
   */
  getPredictedVideos(currentVideoId: string, allVideoIds: string[]): string[] {
    const predictions: Array<{ videoId: string; score: number }> = [];

    allVideoIds.forEach((videoId) => {
      if (videoId !== currentVideoId) {
        const score = this.userBehavior.get(videoId) || 0;
        if (score > 0) {
          predictions.push({ videoId, score });
        }
      }
    });

    return predictions
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((p) => p.videoId);
  }

  /**
   * Preload predicted videos
   */
  async preloadPredictedVideos(
    currentVideoId: string,
    allVideoIds: string[],
  ): Promise<void> {
    const predictedVideos = this.getPredictedVideos(
      currentVideoId,
      allVideoIds,
    );

    if (predictedVideos.length > 0) {
      const thumbnailOptimizer = AdvancedThumbnailOptimizer.getInstance();
      await thumbnailOptimizer.preloadThumbnails(predictedVideos);
      console.log(`🔮 Preloaded ${predictedVideos.length} predicted videos`);
    }
  }
}

/**
 * Advanced caching strategy
 */
export class VideoCache {
  private static instance: VideoCache;
  private cache = new Map<string, any>();
  private maxCacheSize = 50;

  static getInstance(): VideoCache {
    if (!VideoCache.instance) {
      VideoCache.instance = new VideoCache();
    }
    return VideoCache.instance;
  }

  /**
   * Cache video data
   */
  set(key: string, value: any, ttl: number = 300000): void {
    if (this.cache.size >= this.maxCacheSize) {
      // Remove oldest entry
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      value,
      timestamp: Date.now(),
      ttl,
    });
  }

  /**
   * Get cached video data
   */
  get(key: string): any | null {
    const item = this.cache.get(key);
    if (!item) return null;

    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return null;
    }

    return item.value;
  }

  /**
   * Clear expired cache entries
   */
  cleanup(): void {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        this.cache.delete(key);
      }
    }
  }
}

/**
 * Initialize all advanced optimizations
 */
export async function initializeAdvancedVideoOptimization(
  options: AdvancedVideoOptimizationOptions = {},
): Promise<void> {
  const {
    enablePreconnect = true,
    enableDNSPrefetch = true,
    enableResourceHints = true,
    enableServiceWorker = false,
    enablePredictiveLoading = true,
    enableCompression = true,
    enableCaching = true,
    enableBackgroundPreload = true,
  } = options;

  console.log("🚀 Initializing advanced video optimization...");

  // Add resource hints
  if (enableResourceHints) {
    addYouTubeResourceHints();
  }

  // Preload critical resources
  if (enablePreconnect) {
    preloadCriticalYouTubeResources();
  }

  // Initialize YouTube API
  await initializeYouTubeAPI();

  // Initialize optimizers
  const thumbnailOptimizer = AdvancedThumbnailOptimizer.getInstance();
  const connectionOptimizer = ConnectionOptimizer.getInstance();
  const predictiveLoader = PredictiveLoader.getInstance();
  const videoCache = VideoCache.getInstance();

  // Preload critical videos
  if (enableBackgroundPreload) {
    const criticalVideos = ["WjHWcaLFgeI"]; // Main homepage video
    await thumbnailOptimizer.preloadThumbnails(criticalVideos);
  }

  // Set up cache cleanup
  if (enableCaching) {
    setInterval(() => {
      videoCache.cleanup();
    }, 60000); // Clean up every minute
  }

  console.log("✅ Advanced video optimization initialized");
}

/**
 * Get comprehensive optimization settings
 */
export function getComprehensiveOptimizationSettings() {
  const connectionOptimizer = ConnectionOptimizer.getInstance();
  const settings = connectionOptimizer.getOptimalSettings();

  return {
    ...settings,
    enableResourceHints: true,
    enablePredictiveLoading: true,
    enableCaching: true,
    enableBackgroundPreload: true,
    thumbnailOptimizer: AdvancedThumbnailOptimizer.getInstance(),
    predictiveLoader: PredictiveLoader.getInstance(),
    videoCache: VideoCache.getInstance(),
  };
}
